'use client';

import { useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@savvy-kit/components/dialog';
import LinkTabs from '@savvy-kit/patterns/link-tabs';
import { toast } from 'sonner';
import {
  DEFAULT_OVERVIEW_ORDER,
  HIDEABLE_OVERVIEW_SECTION_KEYS,
} from 'src/components/advisor-dashboard/account-overview/overview-panels/overview-section-keys';
import { reconcileSectionOrder } from 'src/components/shared/reorder/reconcile-section-order';

import type { OverviewDraft } from './overview-pane';
import type { TabsDraft } from './tabs-pane';
import { useHouseholdOverviewRailPreference } from '../../../layout/household-overview-rail-preference-context';
import { OverviewPane } from './overview-pane';
import { TabsPane } from './tabs-pane';

type CustomizeHouseholdViewDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Top-level household nav item titles, in their natural (unsaved) order. */
  readonly navTitles: readonly string[];
};

export function CustomizeHouseholdViewDialog({ open, onOpenChange, navTitles }: CustomizeHouseholdViewDialogProps) {
  const [saving, setSaving] = useState(false);

  return (
    <Dialog
      open={open}
      // Escape and an outside press would otherwise dismiss mid-save, stranding the draft the
      // failed-save path exists to keep on screen.
      onOpenChange={(next) => {
        if (saving && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-[480px]">
        <CustomizeHouseholdViewPanes
          key={String(open)}
          navTitles={navTitles}
          saving={saving}
          onSavingChange={setSaving}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

type CustomizeHouseholdViewPanesProps = {
  readonly navTitles: readonly string[];
  readonly saving: boolean;
  readonly onSavingChange: (saving: boolean) => void;
  readonly onClose: () => void;
};

/**
 * Split out and keyed on `open` so every open remounts it and the `useState` initialisers below are
 * the seed. Base UI holds the popup mounted through its exit transition, so its own unmount is too
 * late to be the discard; seeding from an effect instead painted the abandoned draft for a frame.
 */
function CustomizeHouseholdViewPanes({ navTitles, saving, onSavingChange, onClose }: CustomizeHouseholdViewPanesProps) {
  const { order, hidden, tabOrder, saveViewConfig } = useHouseholdOverviewRailPreference();
  const [pane, setPane] = useState<string>('tabs');

  const [overviewDraft, setOverviewDraft] = useState<OverviewDraft>(() => ({
    order: reconcileSectionOrder(order, DEFAULT_OVERVIEW_ORDER),
    hidden: { ...hidden },
  }));
  const [tabsDraft, setTabsDraft] = useState<TabsDraft>(() => ({
    order: reconcileSectionOrder(tabOrder, navTitles),
  }));

  // Both drafts are always fully resolved, so this is a plain index comparison — and dragging back
  // to the natural order by hand disables Reset too, the same as never having touched it.
  const isDefault =
    overviewDraft.order.every((key, index) => key === DEFAULT_OVERVIEW_ORDER[index]) &&
    Object.values(overviewDraft.hidden).every((flag) => !flag) &&
    tabsDraft.order.every((title, index) => title === navTitles[index]);

  function handleReset() {
    setOverviewDraft({ order: DEFAULT_OVERVIEW_ORDER, hidden: {} });
    setTabsDraft({ order: navTitles });
  }

  async function handleSave() {
    onSavingChange(true);
    let succeeded = false;
    try {
      succeeded = await saveViewConfig({
        order: overviewDraft.order,
        // Every hideable key, not just the ones the draft touched: `saveViewConfig` merges `hidden`
        // per key, so a sparse map — Reset's empty one above all — would leave old flags standing.
        hidden: Object.fromEntries(
          HIDEABLE_OVERVIEW_SECTION_KEYS.map((key) => [key, overviewDraft.hidden[key] === true]),
        ),
        tabOrder: withTitlesThisHouseholdLacks(tabsDraft.order, tabOrder),
      });
    } catch {
      // `saveViewConfig` resolves false rather than rejecting, but Cancel, Escape and outside-press
      // are all gated on `saving` — a rejection that escaped would leave no way out of the dialog.
      succeeded = false;
    } finally {
      onSavingChange(false);
    }
    if (!succeeded) {
      toast.error("Couldn't save your view — try again.");
      return;
    }
    onClose();
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Customize views</DialogTitle>
        <DialogDescription>Personalize how households appear across your workspace</DialogDescription>
      </DialogHeader>

      <LinkTabs
        activeTab={pane}
        setActiveTab={setPane}
        tabs={[
          { name: 'Tabs', value: 'tabs' },
          { name: 'Overview', value: 'overview' },
        ]}
      />

      {pane === 'tabs' ? (
        <TabsPane draft={tabsDraft} onChange={setTabsDraft} />
      ) : (
        <OverviewPane draft={overviewDraft} onChange={setOverviewDraft} />
      )}

      <DialogFooter className="flex items-center justify-between sm:justify-between">
        <Button variant="ghost" size="sm" onClick={handleReset} disabled={isDefault}>
          Reset to default
        </Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} isLoading={saving} disabled={saving}>
            Save
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

/**
 * The tab order is one global preference but the nav is per-household — Opportunities, Tax,
 * Paperwork and Retirement planning are all conditional. Seeding reconciles a saved title this
 * household doesn't offer out of the draft, so persisting the draft as-is would drop that title for
 * every household that *does* offer it. Re-inserting at its saved index puts it back.
 */
function withTitlesThisHouseholdLacks(draftOrder: readonly string[], savedOrder: readonly string[]): readonly string[] {
  const inDraft = new Set(draftOrder);
  const merged = [...draftOrder];
  savedOrder.forEach((title, index) => {
    if (inDraft.has(title)) return;
    merged.splice(Math.min(index, merged.length), 0, title);
  });
  return merged;
}
